// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngMiniCart from "@rc/App/Components/XngMiniCart/XngMiniCart";
import setShowDropShadow from "@rc/App/Handlers/XNG/showDropShadow";
import { CustomAppState } from "@rc/App/Store/Reducers";
import Tooltip from "@rc/MobiusOverrides/Tooltip/Tooltip";
import { color, xScreen } from "@rc/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

const mapStateToProps = (state: CustomAppState) => {
    const userName = state.context.session.userName;
    const currentUserIsGuest = getCurrentUserIsGuest(state);

    return {
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        isAuthenticated: userName && !currentUserIsGuest,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    setShowDropShadow,
};

export interface CartLinkStyles {
    titleClickable?: ClickablePresentationProps;
    cartElementStyle?: InjectableCss;
    cartElementIcon?: IconPresentationProps;
    retainedTotalCountToDisplay?: InjectableCss;
}

export const cartLinkStyles: CartLinkStyles = {
    titleClickable: {
        css: css`
            transition: 0.2s;
            font-size: 16px;
            line-height: 20px;
            position: relative;

            &:hover,
            &:focus {
                & > span {
                    color: ${color("primary")};
                }
            }

            ${xScreen("lte", "md")} {
                margin-top: 2px;
            }

            .count-badge {
                height: 15px;
                background: ${color("primary")};
                border-radius: 8px;
                border: 1px solid ${color("common.white")};
                position: absolute;
                top: -10px;
                left: 8px;
                font-size: 10px;
                color: ${color("common.white")};
                text-align: center;
                padding: 1px 4px 1px 3px;
                display: flex;
                align-items: center;
                line-height: 11px;
                justify-content: center;
            }
        `,
    },
    cartElementIcon: {
        size: 18,
        src: "CartHover",
        color: "common.ui4",
        css: css`
            margin-right: 8px;
            @media (max-width: 1024px) {
                &,
                & svg {
                    width: 20px;
                    height: 20px;
                }
            }
        `,
    },
    retainedTotalCountToDisplay: {
        css: css`
            background-color: ${color("secondary")};
            color: ${color("common.white")};
            font-size: 10px;
            font-weight: bold;
            letter-spacing: 1px;
            line-height: 11px;
            padding: 3px 5px;
            margin-right: 8px;
            border-radius: 2px;

            ${xScreen("lte", "md")} {
                margin-right: 0;
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {};
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    loadCurrentCart,
    history,
    setShowDropShadow,
    isAuthenticated,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false, getPromotions: true });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);
    const [isOpen, setIsOpen] = React.useState(false);

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const CartElement = (
        <StyledWrapper className="cart-element" {...styles.cartElementStyle}>
            <Clickable
                {...styles.titleClickable}
                data-test-selector="cartLink"
                aria-label="mini cart"
                onClick={() => history.push(cartUrl as string)}
            >
                <Typography
                    data-test-selector="header_cart_text"
                    weight={500}
                    css={css`
                        color: ${isOpen ? color("primary") : color("text")};
                        display: flex;
                        align-items: end;
                    `}
                >
                    <Icon {...styles.cartElementIcon} className={isOpen ? `opened` : ""} />
                    {isAuthenticated && <span className="count-badge">{retainedTotalCountToDisplay}</span>}
                    <Hidden
                        css={css`
                            display: inline;
                        `}
                        below="lg"
                    >
                        {translate("Cart")}
                    </Hidden>
                </Typography>
            </Clickable>
        </StyledWrapper>
    );

    const CartOverlay = () => <XngMiniCart />;

    return (
        <StyledWrapper>
            <Hidden below="lg">
                <StyledWrapper>
                    <Tooltip
                        trigger="mouseenter focus"
                        onOpen={() => {
                            hideAll();
                            setTimeout(() => setShowDropShadow({ showDropShadow: true }));
                            setIsOpen(true);
                        }}
                        onClose={() => {
                            setShowDropShadow({ showDropShadow: false });
                            setIsOpen(false);
                        }}
                        placement="bottom-end"
                        triggerAltText={translate("Mini Cart Menu")}
                        triggerComponent={CartElement}
                        delay={[100, 350]}
                        offset={[0, 8]}
                        cssOverrides={{
                            tooltipBody: css``,
                            tooltipContainer: css`
                                &&&& {
                                    max-width: initial;
                                    width: 437px;
                                    padding: 0;
                                    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
                                    border-radius: 0;
                                    border: 1px solid ${color("common.ui3")};
                                    overflow: hidden;
                                    margin-top: 11px;
                                    .tippy-arrow {
                                        top: 5px;
                                        left: -8px !important;
                                    }
                                }
                            `,
                        }}
                        passAsChildren={true}
                        disabled={!isAuthenticated}
                    >
                        <CartOverlay />
                    </Tooltip>
                </StyledWrapper>
            </Hidden>
            <Hidden above="md">{CartElement}</Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withHistory(CartLink))),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [],
        group: "Common",
    },
};

export default widgetModule;
